import {
  ActionField,
  ActionFieldChildProps,
  ActionFieldProps,
  AsyncField,
  Field,
  useFormContext,
} from '@oscar/form';
import { Col, Icon, Input, Row, Select, Tooltip } from '@oscar/theme';
import { InfoCircleOutlined } from '@ant-design/icons';
import { DatePickerPopulate, FileUploaded, TextField } from 'common/components';
import { FORMAT_DATE } from 'common/constants';
import { APIS } from 'common/constants/endpoints';
import { useAuth } from 'features/auth';
import { getDateCorrect, resetDate } from 'features/insulation/utils';
import { debounce, filter, find, uniq } from 'lodash';
import moment from 'moment';
import { StatusInstance } from 'views/pages/create-instrulation/hooks/useStatus';
import CoatList from './components/CoatList';
const { TextArea } = Input;
const { Option } = Select;

interface Props {
  statusRequest: StatusInstance;
}

function MultiSelect(props: { label: string; value?: any; mappingData?: any }) {
  const { label, value, mappingData } = props;

  const transferData = (data: any, oldValue: any) => {
    const newValue = find(data, (item: any) => {
      const compareValue = item.id || item.value;
      return compareValue === oldValue;
    });
    return newValue?.label || oldValue;
  };

  return (
    <div className="text-field">
      <div className="text-label">{label}</div>
      <div className="text-description">
        {value?.length > 3 ? (
          <Tooltip
            placement="bottom"
            title={value?.map((item: any, key: any) => (
              <div key={`${item}${key}`}>
                {mappingData && transferData(mappingData, item)}
              </div>
            ))}
          >
            <span>
              {value?.length} Coating Test Required &nbsp;&nbsp;
              <InfoCircleOutlined />
            </span>
          </Tooltip>
        ) : (
          value?.map((item: any, key: any) => (
            <div key={`${item}${key}`}>
              {mappingData && transferData(mappingData, item)}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export const PaintingDetail = ({
  statusRequest: { status, state, id },
}: Props) => {
  const [formContext] = useFormContext();
  const [auth] = useAuth();
  const metaDataPaintingValidation =
    formContext.selectors?.getDataByKey('metaDataPaintingValidationDetail') ||
    {};
  const metaDataInsulation =
    formContext.selectors?.getDataByKey('metaDataInsulationDetail') || {};

  const handleChangeCoatingSystem = () => {
    formContext?.form?.setFieldsValue({ serviceCategory: undefined });
    formContext?.form?.setFieldsValue({ environment: undefined });
  };

  const handleChangeServiceCategory = () => {
    formContext?.form?.setFieldsValue({ environment: undefined });
  };

  const disableSiteVisitDate = (current: any) => {
    const startDate = formContext.form?.getFieldValue('startDate');
    const endDate = formContext.form?.getFieldValue('endDate');

    if (startDate) {
      return (
        (current && current.isBefore(moment(), 'day')) ||
        current.isAfter(getDateCorrect(startDate), 'day')
      );
    }
    if (endDate) {
      return (
        (current && current.isAfter(getDateCorrect(endDate))) ||
        current.isBefore(moment(), 'day')
      );
    }
    return current && current.isBefore(moment(), 'day');
  };

  const disableEndDate = (current: any) => {
    const siteVisitDate = formContext.form?.getFieldValue('siteVisitDate');
    const startDate = formContext.form?.getFieldValue('startDate');
    if (startDate && getDateCorrect(startDate).isAfter(moment(), 'day')) {
      return current && current.isBefore(getDateCorrect(startDate), 'day');
    } else if (
      siteVisitDate &&
      getDateCorrect(siteVisitDate).isAfter(moment(), 'day')
    ) {
      return current && current.isBefore(getDateCorrect(siteVisitDate), 'day');
    } else {
      return current && current.isBefore(moment(), 'day');
    }
  };

  const disableStartDate = (current: any) => {
    const siteVisitDate = formContext.form?.getFieldValue('siteVisitDate');
    const endDate = formContext.form?.getFieldValue('endDate');

    if (siteVisitDate || endDate) {
      return (
        (current &&
          (siteVisitDate &&
          getDateCorrect(siteVisitDate).isAfter(moment(), 'day')
            ? current.isBefore(getDateCorrect(siteVisitDate), 'day')
            : current.isBefore(moment(), 'day'))) ||
        current.isAfter(getDateCorrect(endDate), 'day')
      );
    }
    return current && current.isBefore(getDateCorrect(moment()), 'day');
  };

  return (
    <div className="requestor-detail">
      <ActionField
        name="resetDate"
        request={{
          name: 'resetDate',
        }}
      >
        {(actionProps: any) => {
          if (
            status === 'DRAFT' &&
            auth.checkRole('PENDING_VALIDTION', 'EDITED')
          ) {
            resetDate(actionProps?.field, state);
          }
          return <></>;
        }}
      </ActionField>
      <>
        {auth.checkRole('PENDING_VALIDTION', 'SUBMITTED', 'PAINTING') &&
        state === 'EDIT' ? (
          <Row gutter={20}>
            <Col span={12}>
              <AsyncField
                name={'fetchWorkAssigned'}
                requests={[
                  {
                    name: 'workAssigned',
                    url: APIS.SEARCH_SERVICE_PROVIDER,
                    method: 'GET',
                    nextParams: (nextProps: any) => {
                      if (nextProps?.customData?.textSearch?.length) {
                        return {
                          limit: 6,
                          name: nextProps?.customData?.textSearch || '',
                        };
                      }
                      return {};
                    },
                  },
                ]}
              >
                {(workAssignedProps: any) => {
                  const options = workAssignedProps?.data?.workAssigned || [];
                  const textSearch =
                    workAssignedProps?.requests?.[0]?.params?.name || '';

                  return (
                    <Row gutter={15}>
                      <Col span={24}>
                        <p className="label-normal">
                          Painting Work Assigned to
                        </p>

                        <Field name={['workAssigned']}>
                          <Select
                            value={textSearch}
                            showSearch
                            suffixIcon={<Icon icon="search" size={18} />}
                            className="select-search"
                            placeholder="Select Service Provider"
                            allowClear
                            filterOption={false}
                            onSearch={debounce(value => {
                              workAssignedProps.reloadData({
                                textSearch: value,
                              });
                            }, 300)}
                          >
                            {options.map?.((item: any) => {
                              return (
                                <Option
                                  value={item.serviceProviderId}
                                  key={item.serviceProviderId}
                                >
                                  {item.companyName}
                                </Option>
                              );
                            })}
                          </Select>
                        </Field>
                      </Col>
                    </Row>
                  );
                }}
              </AsyncField>
            </Col>
            <Col span={12}>
              <p className="label-normal">MLNG CI Assigned</p>
              {/* <Field name={['mlngciAssignedName']}>
                <Input maxLength={100} placeholder={`MLNG CI Assigned`} />
              </Field> */}
              <AsyncField
                name={'fetchInspectorName'}
                requests={[
                  {
                    name: 'inspectorName',
                    url: APIS.AZURE_AD_USER,
                    method: 'POST',
                    nextParams: (nextProps: any) => {
                      return {
                        limit: 6,
                        name: nextProps?.customData?.textSearch || '',
                        type: -1,
                      };
                    },
                  },
                ]}
              >
                {(inspectorNameProps: any) => {
                  const options = inspectorNameProps?.data?.inspectorName || [];
                  const textSearch =
                    inspectorNameProps?.requests?.[0]?.params?.name || '';

                  return (
                    <>
                      <Field name={['mlngciAssignedName']}>
                        <Select
                          value={textSearch}
                          showSearch
                          suffixIcon={<Icon icon="search" size={18} />}
                          className="select-search"
                          placeholder="Search Directory"
                          allowClear
                          filterOption={false}
                          onSearch={debounce(value => {
                            inspectorNameProps.reloadData({
                              textSearch: value,
                            });
                          }, 300)}
                        >
                          {options.map?.((item: any) => {
                            return (
                              <Option
                                value={item.displayName}
                                key={item.displayName}
                              >
                                {item?.displayName}
                              </Option>
                            );
                          })}
                        </Select>
                      </Field>
                    </>
                  );
                }}
              </AsyncField>
            </Col>
          </Row>
        ) : (
          status !== 'DRAFT' &&
          !auth.checkRole('PENDING_VALIDTION', 'EDITED') && (
            <Row gutter={20}>
              <Col span={12}>
                <Field name={['workAssignedName']}>
                  <TextField label="Painting Work Assigned to" />
                </Field>
              </Col>
              <Col span={12}>
                <Field name={['mlngciAssignedName']}>
                  <TextField label="MLNG CI Assigned" />
                </Field>
              </Col>
            </Row>
          )
        )}

        <Row gutter={[15, 15]}>
          <Col span={12}>
            {state === 'VIEW' || status === 'IN_SERVICE' ? (
              <Field name="area">
                <TextField
                  label="Area"
                  mappingData={metaDataInsulation?.area}
                />
              </Field>
            ) : (
              <>
                <p>Area</p>
                <Field name="area">
                  <Select placeholder="Select area" className="area" allowClear>
                    {metaDataInsulation?.area?.map?.(
                      (item: any, index: number) => (
                        <Option key={`${item.id}-${index}`} value={item.id}>
                          {item?.label}
                        </Option>
                      ),
                    )}
                  </Select>
                </Field>
              </>
            )}
          </Col>
          <Col span={12}>
            {state === 'VIEW' || status === 'IN_SERVICE' ? (
              <Field name="siteVisitDate">
                <TextField
                  label="Site Visit Date"
                  isDate
                  formartDate={FORMAT_DATE.FULL_DATETIME_HOUR}
                />
              </Field>
            ) : (
              <>
                <p>Site Visit Date</p>
                <Field name="siteVisitDate" dependencies={['startDate']}>
                  <DatePickerPopulate
                    showTime={{ format: 'HH:mm A', use12Hours: true }}
                    format={FORMAT_DATE.FULL_DATETIME_HOUR}
                    disabledDate={disableSiteVisitDate}
                    inputReadOnly
                  />
                </Field>
              </>
            )}
          </Col>
        </Row>
        <Row gutter={[15, 15]}>
          <Col span={12}>
            {state === 'VIEW' ? (
              <Field name="startDate">
                <TextField
                  label="Start Date"
                  isDate
                  formartDate={FORMAT_DATE.SHORT_MONTH_DATE}
                />
              </Field>
            ) : (
              <>
                <p>Start Date</p>
                <Field name="startDate" dependencies={['siteVisitDate']}>
                  <DatePickerPopulate
                    disabledDate={disableStartDate}
                    format={FORMAT_DATE.SHORT_MONTH_DATE}
                    inputReadOnly
                  />
                </Field>
              </>
            )}
          </Col>
          <Col span={12}>
            {state === 'VIEW' ? (
              <Field name="endDate">
                <TextField
                  label="End Date"
                  isDate
                  formartDate={FORMAT_DATE.SHORT_MONTH_DATE}
                />
              </Field>
            ) : (
              <>
                <p>End Date</p>
                <Field
                  name="endDate"
                  dependencies={['siteVisitDate', 'startDate']}
                >
                  <DatePickerPopulate
                    disabledDate={disableEndDate}
                    format={FORMAT_DATE.SHORT_MONTH_DATE}
                    inputReadOnly
                  />
                </Field>
              </>
            )}
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            {state === 'VIEW' ? (
              <Field name={['workDescription']}>
                <TextField label="Work Description" />
              </Field>
            ) : (
              <>
                <p>Work Description</p>
                <Field name={['workDescription']}>
                  <TextArea
                    placeholder="Maximum 250 characters"
                    autoSize={{ minRows: 6, maxRows: 5 }}
                    className="work-description"
                    maxLength={250}
                  />
                </Field>
              </>
            )}
          </Col>
        </Row>
        <Row gutter={20}>
          <Col span={8}>
            {state === 'VIEW' ? (
              <Field name={['coatingSystem']}>
                <TextField
                  label="Coating System (PTS 15.20.03 Protective Coatings and Linings)"
                  hightLabel={true}
                  mappingData={metaDataPaintingValidation?.coatingSystem}
                />
              </Field>
            ) : (
              <>
                <p style={{ height: '44px' }}>
                  Coating System (PTS 15.20.03 Protective Coatings and Linings)
                </p>
                <Field name={['coatingSystem']}>
                  <Select
                    size="large"
                    placeholder="Select coating System"
                    dropdownClassName="certain-category-search-dropdown"
                    allowClear
                    onChange={handleChangeCoatingSystem}
                  >
                    {metaDataPaintingValidation?.coatingSystem?.map?.(
                      (item: any, index: number) => (
                        <Option key={`${item.id}-${index}`} value={item?.id}>
                          {item.label}
                        </Option>
                      ),
                    )}
                  </Select>
                </Field>
              </>
            )}
          </Col>
          <Col span={8}>
            {state === 'VIEW' ? (
              <Field name={['serviceCategory']}>
                <TextField
                  label="Service Category"
                  hightLabel={true}
                  mappingData={metaDataPaintingValidation?.serviceCategory}
                />
              </Field>
            ) : (
              <>
                <p style={{ height: '44px' }}>Service Category</p>
                <ActionField
                  name="checkServiceCategory"
                  request={{ name: 'checkServiceCategory' }}
                  dependencies={['coatingSystem']}
                >
                  {(actionProps: ActionFieldChildProps) => {
                    const { coatingSystem } = actionProps?.otherFields || {};
                    const filteredData = uniq(
                      metaDataPaintingValidation?.coatingSystemConstants
                        ?.filter(
                          (item: any) => item.coatingSystemId === coatingSystem,
                        )
                        .map((item: any) => item.serviceCategoryId),
                    );
                    return (
                      <Field
                        name={['serviceCategory']}
                        dependencies={['coatingSystem']}
                      >
                        <Select
                          size="large"
                          placeholder="Select Service Category"
                          dropdownClassName="certain-category-search-dropdown"
                          allowClear
                          disabled={!coatingSystem}
                          onChange={handleChangeServiceCategory}
                        >
                          {metaDataPaintingValidation?.serviceCategory
                            ?.filter((item: any) =>
                              filteredData.includes(item?.id),
                            )
                            ?.map?.((item: any, index: number) => (
                              <Option
                                key={`${item.id}-${index}`}
                                value={item?.id}
                              >
                                {item.label}
                              </Option>
                            ))}
                        </Select>
                      </Field>
                    );
                  }}
                </ActionField>
              </>
            )}
          </Col>
          <Col span={8}>
            {state === 'VIEW' ? (
              <Field name={['environment']}>
                <TextField
                  label="Environment"
                  mappingData={metaDataPaintingValidation?.environemnt}
                  hightLabel={true}
                />
              </Field>
            ) : (
              <>
                <p style={{ height: '44px' }}>Environment</p>
                <ActionField
                  name="checkServiceCategory"
                  request={{ name: 'checkServiceCategory' }}
                  dependencies={['serviceCategory', 'coatingSystem']}
                >
                  {(actionProps: ActionFieldChildProps) => {
                    const { serviceCategory, coatingSystem } =
                      actionProps?.otherFields || {};
                    const filteredData = uniq(
                      filter(
                        metaDataPaintingValidation?.coatingSystemConstants,
                        {
                          serviceCategoryId: serviceCategory,
                          coatingSystemId: coatingSystem,
                        },
                      ).map((item: any) => item.environmentId),
                    );
                    return (
                      <Field
                        name={['environment']}
                        dependencies={['serviceCategory', 'coatingSystem']}
                      >
                        <Select
                          size="large"
                          placeholder="Select Environment"
                          dropdownClassName="certain-category-search-dropdown"
                          allowClear
                          disabled={!serviceCategory || !coatingSystem}
                        >
                          {metaDataPaintingValidation?.environemnt
                            ?.filter((item: any) =>
                              filteredData.includes(item?.id),
                            )
                            ?.map?.((item: any, index: number) => (
                              <Option
                                key={`${item.id}-${index}`}
                                value={item?.id}
                              >
                                {item.label}
                              </Option>
                            ))}
                        </Select>
                      </Field>
                    );
                  }}
                </ActionField>
              </>
            )}
          </Col>
        </Row>

        <Row gutter={20}>
          <Col span={8}>
            {state === 'VIEW' ? (
              <Field name={['systemId']}>
                <TextField
                  label="System ID"
                  mappingData={metaDataPaintingValidation?.systemId}
                />
              </Field>
            ) : (
              auth.checkRole('PENDING_VALIDTION', 'SUBMITTED', 'PAINTING') && (
                <>
                  <p>System ID</p>
                  <ActionField
                    name="checkSystemId"
                    request={{ name: 'checkServiceCategory' }}
                    dependencies={[
                      'environment',
                      'coatingSystem',
                      'serviceCategory',
                    ]}
                  >
                    {(actionProps: ActionFieldChildProps) => {
                      const { environment, coatingSystem, serviceCategory } =
                        actionProps?.otherFields || {};

                      const filteredData = uniq(
                        filter(
                          metaDataPaintingValidation?.coatingSystemConstants,
                          {
                            coatingSystemId: coatingSystem,
                            environmentId: environment,
                            serviceCategoryId: serviceCategory,
                          },
                        ).map((item: any) => item.systemId),
                      );
                      return (
                        <Field name={['systemId']}>
                          <Select
                            size="large"
                            placeholder="Select System ID"
                            dropdownClassName="certain-category-search-dropdown"
                            allowClear
                            disabled={
                              !serviceCategory || !coatingSystem || !environment
                            }
                          >
                            {metaDataPaintingValidation?.systemId
                              ?.filter((item: any) =>
                                filteredData.includes(item?.id),
                              )
                              ?.map?.((item: any, index: number) => (
                                <Option
                                  key={`${item.id}-${index}`}
                                  value={item.id}
                                >
                                  {item?.label}
                                </Option>
                              ))}
                          </Select>
                        </Field>
                      );
                    }}
                  </ActionField>
                </>
              )
            )}
          </Col>
          <Col span={8}>
            {state === 'VIEW' ? (
              <Field name={['surfacePreparation']}>
                <TextField
                  label="Surface Preparation"
                  mappingData={metaDataPaintingValidation?.surfacePreparation}
                />
              </Field>
            ) : (
              auth.checkRole('PENDING_VALIDTION', 'SUBMITTED', 'PAINTING') && (
                <>
                  <p>Surface Preparation</p>
                  <Field name={['surfacePreparation']}>
                    <Select
                      size="large"
                      placeholder="Select Surface Preparation"
                      dropdownClassName="certain-category-search-dropdown"
                      allowClear
                    >
                      {metaDataPaintingValidation?.surfacePreparation?.map?.(
                        (item: any, index: number) => (
                          <Option key={`${item.id}-${index}`} value={item?.id}>
                            {item.label}
                          </Option>
                        ),
                      )}
                    </Select>
                  </Field>
                </>
              )
            )}
          </Col>
          <Col span={8}>
            {state === 'VIEW' ? (
              <Field name={['coatingTestRequired']}>
                <MultiSelect
                  label="Coating Test Required (Optional)"
                  mappingData={metaDataPaintingValidation?.coatingTest}
                />
              </Field>
            ) : (
              auth.checkRole('PENDING_VALIDTION', 'SUBMITTED', 'PAINTING') && (
                <>
                  <p>Coating Test Required (Optional)</p>
                  <Field name={['coatingTestRequired']}>
                    <Select
                      mode="multiple"
                      size="large"
                      placeholder="Select Coating Test"
                      dropdownClassName="certain-category-search-dropdown"
                      allowClear
                    >
                      {metaDataPaintingValidation?.coatingTest?.map?.(
                        (item: any, index: number) => (
                          <Option key={`${item.id}-${index}`} value={item?.id}>
                            {item.label}
                          </Option>
                        ),
                      )}
                    </Select>
                  </Field>
                </>
              )
            )}
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <ActionField
              name="coatList"
              request={{ name: 'coatList' }}
              dependencies={[
                'systemId',
                'environment',
                'coatingSystem',
                'serviceCategory',
              ]}
            >
              {(actionProps: ActionFieldProps) => {
                const {
                  systemId,
                  environment,
                  coatingSystem,
                  serviceCategory,
                } = actionProps?.otherFields || {};

                return (
                  <CoatList
                    masterData={
                      metaDataPaintingValidation?.coatingSystemConstants
                    }
                    dependencies={{
                      coatingSystemId: coatingSystem,
                      environmentId: environment,
                      serviceCategoryId: serviceCategory,
                      systemId,
                    }}
                    name={['otherCoatsList']}
                    readOnly={
                      state === 'VIEW' || status !== 'PENDING_VALIDTION'
                        ? true
                        : false
                    }
                  />
                );
              }}
            </ActionField>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <FileUploaded
              name="attachments"
              title="Attachment (optional)"
              request={{
                name: 'attachment',
                url: APIS.UPLOAD_FILE_TO_AZURE,
                method: 'POST',
              }}
              readOnly={state === 'VIEW'}
            />
          </Col>
        </Row>
      </>
    </div>
  );
};

export default PaintingDetail;
